import type { CSSProperties } from "react";

/*
 * Visual system ported from the prototype (App.jsx). The default sage palette
 * is seeded into config and applied at runtime through applyPalette(), so these
 * var(--token) references are the single source of color. The :root block below
 * is only a fallback for first paint before config loads.
 */

export const css = `
:root{
  --bg:#FBFAF8; --surface:#FFFFFF; --ink:#1C1B19; --muted:#86837C;
  --line:#ECE9E3; --accent:#5B6B4F; --accent-d:#46553B; --accent-soft:#EDF0E7;
  --block:#E9DCCB; --danger:#B23B2E; --shadow:0 1px 2px rgba(28,27,25,.05),0 8px 28px rgba(28,27,25,.06);
}
*{box-sizing:border-box}
html,body{margin:0}
button{font-family:inherit;cursor:pointer;border:none;background:none}
a{color:var(--accent)}
.fade{animation:fade .45s cubic-bezier(.16,1,.3,1)}
@keyframes fade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.input:focus-visible,.seg:focus-visible,.daybtn:focus-visible,.slot:focus-visible,.iconbtn:focus-visible,
.primary:focus-visible,.ghost:focus-visible,.chip:focus-visible,.link:focus-visible,.select:focus-visible,
.swatchbtn:focus-visible,.preset:focus-visible,.switch:focus-within{outline:2.5px solid var(--accent);outline-offset:2px}
.input{transition:border-color .15s,box-shadow .15s}
.input:focus{outline:none;border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-soft)}
.daybtn:not(:disabled):hover{background:var(--accent-soft)}
.slot:not(:disabled):hover{border-color:var(--accent);transform:translateY(-1px)}
.primary:hover{background:var(--accent-d)}
.ghost:hover{background:var(--accent-soft)}
.chip:hover{border-color:var(--accent)}
.preset:hover{border-color:var(--accent)}
.iconbtn:hover{background:var(--accent-soft)}
.link:hover{color:var(--accent-d);text-decoration:underline}
.seg{transition:color .2s}
.sr-check{position:absolute;width:1px;height:1px;opacity:0}
@media (prefers-reduced-motion:reduce){*{animation:none!important;transition:none!important}}
@media (max-width:860px){
  .bookgrid,.admingrid{grid-template-columns:1fr!important}
}
`;

export const styles: Record<string, CSSProperties> = {
  root: {
    minHeight: "100vh",
    background: "var(--bg)",
    color: "var(--ink)",
    fontFamily:
      "-apple-system,BlinkMacSystemFont,'SF Pro Text','Segoe UI',Roboto,sans-serif",
    WebkitFontSmoothing: "antialiased",
  },
  srOnly: { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" },
  header: {
    position: "sticky", top: 0, zIndex: 20, display: "flex", alignItems: "center",
    justifyContent: "space-between", padding: "14px clamp(16px,4vw,40px)",
    background: "rgba(251,250,248,.8)", backdropFilter: "saturate(180%) blur(20px)",
    borderBottom: "1px solid var(--line)",
  },
  brand: { display: "flex", alignItems: "center", gap: 9, textDecoration: "none", color: "var(--ink)" },
  mark: {
    color: "var(--accent)", display: "grid", placeItems: "center", width: 34, height: 34,
    background: "var(--accent-soft)", borderRadius: 10, overflow: "hidden",
  },
  logoImg: { width: 34, height: 34, borderRadius: 10, objectFit: "cover" },
  brandName: { fontSize: 19, fontWeight: 680, letterSpacing: "-.02em" },
  brandSub: { fontSize: 14, color: "var(--muted)", fontWeight: 500, marginLeft: -3 },
  nav: { display: "flex", alignItems: "center" },
  segmented: { display: "flex", background: "#EFEDE8", borderRadius: 11, padding: 3, gap: 2 },
  seg: { padding: "7px 18px", fontSize: 14.5, fontWeight: 560, color: "var(--muted)", borderRadius: 8, textDecoration: "none", display: "inline-block" },
  segOn: { background: "var(--surface)", color: "var(--ink)", boxShadow: "0 1px 3px rgba(0,0,0,.08)" },
  main: { maxWidth: 1080, margin: "0 auto", padding: "clamp(24px,5vw,52px) clamp(16px,4vw,40px) 80px" },

  heroWrap: { marginBottom: 30, maxWidth: 640 },
  eyebrow: {
    fontSize: 13, fontWeight: 620, letterSpacing: ".08em", textTransform: "uppercase",
    color: "var(--accent)", margin: "0 0 10px",
  },
  h1: {
    fontSize: "clamp(30px,5vw,44px)", lineHeight: 1.05, letterSpacing: "-.03em",
    fontWeight: 700, margin: "0 0 12px",
  },
  lede: { fontSize: 17, lineHeight: 1.5, color: "var(--muted)", margin: 0 },

  banner: {
    display: "flex", gap: 12, alignItems: "flex-start", background: "var(--accent-soft)",
    border: "1px solid var(--line)", borderRadius: 14, padding: "14px 16px", marginBottom: 24,
  },
  bannerDot: { width: 9, height: 9, borderRadius: "50%", background: "var(--accent)", marginTop: 6, flexShrink: 0 },
  bannerTitle: { fontSize: 14, display: "block", marginBottom: 2 },
  bannerBody: { fontSize: 15, lineHeight: 1.45, color: "var(--ink)", margin: 0 },

  bookGrid: { display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 20, alignItems: "start" },
  adminGrid: { display: "grid", gridTemplateColumns: "1.05fr 1fr", gap: 20, alignItems: "start" },
  adminRight: { display: "flex", flexDirection: "column", gap: 20 },
  adminLeft: { display: "flex", flexDirection: "column", gap: 20 },

  card: {
    background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 18,
    padding: "clamp(18px,2.5vw,24px)", boxShadow: "var(--shadow)",
  },
  cardTitle: { fontSize: 19, fontWeight: 650, letterSpacing: "-.02em", margin: "0 0 4px" },
  sub: { fontSize: 14.5, color: "var(--muted)", margin: "0 0 16px", lineHeight: 1.45 },

  calHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 },
  calNav: { display: "flex", gap: 6 },
  iconBtn: {
    width: 34, height: 34, borderRadius: 9, fontSize: 20, color: "var(--ink)",
    display: "grid", placeItems: "center", border: "1px solid var(--line)", lineHeight: 1,
  },
  weekRow: { display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 6 },
  weekCell: { textAlign: "center", fontSize: 12, fontWeight: 600, color: "var(--muted)", padding: "4px 0" },
  dayGridRows: { display: "flex", flexDirection: "column", gap: 4 },
  dayGrid: { display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 },
  day: {
    aspectRatio: "1", borderRadius: 11, fontSize: 15, fontWeight: 520, color: "var(--ink)",
    position: "relative", display: "grid", placeItems: "center", transition: "background .15s,transform .1s",
  },
  dayMuted: { color: "transparent", pointerEvents: "none" },
  dayOff: { color: "#C3BFB6", textDecoration: "none", cursor: "not-allowed" },
  daySel: { background: "var(--accent)", color: "#fff", fontWeight: 620 },
  dayToday: { border: "1.5px solid var(--accent)" },
  blockDot: { position: "absolute", bottom: 7, width: 5, height: 5, borderRadius: "50%", background: "var(--block)" },
  fullDot: { position: "absolute", bottom: 7, width: 5, height: 5, borderRadius: "50%", background: "var(--muted)" },
  legend: { display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap" },
  legendItem: { display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--muted)" },
  swatch: { width: 12, height: 12, borderRadius: 4, display: "inline-block" },

  slotWrap: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(96px,1fr))", gap: 8, marginTop: 4 },
  slot: {
    padding: "12px 8px", borderRadius: 12, border: "1.5px solid var(--line)", background: "var(--surface)",
    fontSize: 14.5, fontWeight: 560, color: "var(--ink)", transition: "all .15s",
    display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
  },
  slotOn: { background: "var(--accent)", borderColor: "var(--accent)", color: "#fff" },
  slotTaken: { background: "#F5F3EF", color: "#BBB7AE", cursor: "not-allowed", borderColor: "transparent" },
  takenTag: { fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" },

  formWrap: { marginTop: 22, paddingTop: 22, borderTop: "1px solid var(--line)" },
  formHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 },
  formTitle: { fontSize: 17, fontWeight: 640, margin: 0, letterSpacing: "-.01em" },
  formMeta: { fontSize: 14, color: "var(--accent)", fontWeight: 580, margin: 0 },
  field: { marginBottom: 14 },
  label: { display: "block", fontSize: 13.5, fontWeight: 580, marginBottom: 6 },
  input: {
    width: "100%", padding: "11px 13px", fontSize: 15.5, borderRadius: 11,
    border: "1.5px solid var(--line)", background: "var(--surface)", color: "var(--ink)",
  },
  inputErr: { borderColor: "var(--danger)" },
  errText: { display: "block", fontSize: 13, color: "var(--danger)", marginTop: 5 },
  hint: { display: "block", fontSize: 13, color: "var(--muted)", marginTop: 5 },

  checkRow: { display: "flex", alignItems: "flex-start", gap: 10, margin: "4px 0 14px", cursor: "pointer" },
  checkBox: { marginTop: 2, width: 18, height: 18, accentColor: "var(--accent)", flexShrink: 0 },
  checkLabel: { fontSize: 14, lineHeight: 1.4, color: "var(--ink)" },
  forgetBtn: { background: "none", color: "var(--muted)", fontSize: 13, fontWeight: 540, textDecoration: "underline", marginLeft: "auto" },

  primary: {
    width: "100%", padding: "13px", background: "var(--accent)", color: "#fff",
    fontSize: 15.5, fontWeight: 600, borderRadius: 12, transition: "background .15s", marginTop: 4,
  },
  primarySm: {
    padding: "10px 18px", background: "var(--accent)", color: "#fff", fontSize: 14.5,
    fontWeight: 580, borderRadius: 10, whiteSpace: "nowrap",
  },
  ghost: {
    width: "100%", padding: "13px", background: "transparent", color: "var(--accent-d)",
    fontSize: 15.5, fontWeight: 580, borderRadius: 12, border: "1.5px solid var(--line)",
  },
  ghostSm: {
    padding: "10px 16px", background: "transparent", color: "var(--muted)", fontSize: 14,
    fontWeight: 560, borderRadius: 10, border: "1.5px solid var(--line)",
  },
  linkBtn: {
    background: "none", color: "var(--accent)", fontSize: 14.5, fontWeight: 560,
    margin: "18px auto 0", display: "block",
  },

  confirmWrap: { display: "grid", placeItems: "center", minHeight: "60vh" },
  confirmCard: {
    background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 22,
    padding: "clamp(26px,4vw,40px)", maxWidth: 460, width: "100%", textAlign: "center", boxShadow: "var(--shadow)",
  },
  check: {
    width: 62, height: 62, borderRadius: "50%", background: "var(--accent)",
    display: "grid", placeItems: "center", margin: "0 auto 18px",
  },
  confirmTitle: { fontSize: 27, fontWeight: 700, letterSpacing: "-.02em", margin: "0 0 6px" },
  confirmLede: { fontSize: 16, color: "var(--muted)", margin: "0 0 22px" },
  summary: { textAlign: "left", background: "var(--bg)", borderRadius: 14, padding: "6px 16px", margin: "0 0 18px" },
  sumRow: { display: "flex", justifyContent: "space-between", padding: "11px 0", borderBottom: "1px solid var(--line)" },
  sumK: { fontSize: 14, color: "var(--muted)", margin: 0 },
  sumV: { fontSize: 14.5, fontWeight: 580, margin: 0, textAlign: "right" },
  confirmNote: { fontSize: 14, color: "var(--muted)", lineHeight: 1.5, margin: "0 0 18px" },
  confirmBtns: { display: "flex", flexDirection: "column", gap: 10 },

  hoursList: { display: "flex", flexDirection: "column", gap: 2 },
  hourRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
    padding: "9px 0", borderBottom: "1px solid var(--line)", flexWrap: "wrap",
  },
  toggleWrap: { display: "flex", alignItems: "center", gap: 11, cursor: "pointer", position: "relative" },
  switch: {
    width: 44, height: 27, borderRadius: 14, background: "#D8D5CE", position: "relative",
    transition: "background .2s", flexShrink: 0, display: "inline-block",
  },
  switchOn: { background: "var(--accent)" },
  knob: {
    position: "absolute", top: 3, left: 3, width: 21, height: 21, borderRadius: "50%",
    background: "#fff", transition: "transform .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)",
  },
  knobOn: { transform: "translateX(17px)" },
  dayName: { fontSize: 15, fontWeight: 540, minWidth: 86 },
  hourTimes: { display: "flex", alignItems: "center", gap: 8, transition: "opacity .2s" },
  toDash: { fontSize: 13, color: "var(--muted)" },
  select: {
    padding: "8px 10px", fontSize: 14, borderRadius: 9, border: "1.5px solid var(--line)",
    background: "var(--surface)", color: "var(--ink)", fontWeight: 530,
  },

  slotLenRow: { marginTop: 18 },
  chipRow: { display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" },
  chip: {
    padding: "8px 15px", borderRadius: 10, border: "1.5px solid var(--line)",
    fontSize: 14, fontWeight: 560, color: "var(--ink)", background: "var(--surface)",
  },
  chipOn: { background: "var(--accent)", borderColor: "var(--accent)", color: "#fff" },

  textarea: {
    width: "100%", padding: "12px 13px", fontSize: 15, borderRadius: 12,
    border: "1.5px solid var(--line)", background: "var(--surface)", color: "var(--ink)",
    resize: "vertical", fontFamily: "inherit", lineHeight: 1.5,
  },
  msgActions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 },
  previewBox: { marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--line)" },
  previewLabel: {
    fontSize: 12, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase",
    letterSpacing: ".06em", display: "block", marginBottom: 8,
  },
  bannerMini: {
    display: "flex", gap: 10, alignItems: "flex-start", background: "var(--accent-soft)",
    borderRadius: 11, padding: "11px 13px",
  },

  addRow: { display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" },
  dateInput: {
    flex: 1, padding: "10px 13px", fontSize: 15, borderRadius: 11,
    border: "1.5px solid var(--line)", background: "var(--surface)", color: "var(--ink)", minWidth: 150,
  },
  blockList: { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 2 },
  blockItem: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "11px 0", borderBottom: "1px solid var(--line)", fontSize: 14.5, fontWeight: 520,
  },
  removeBtn: { color: "var(--danger)", fontSize: 13.5, fontWeight: 560 },

  empty: { textAlign: "center", padding: "44px 16px" },
  emptyMark: {
    width: 46, height: 46, borderRadius: 13, background: "var(--bg)", display: "grid",
    placeItems: "center", margin: "0 auto 14px", border: "1px solid var(--line)",
  },
  emptyTitle: { fontSize: 16, fontWeight: 600, margin: "0 0 4px" },
  emptyBody: { fontSize: 14, color: "var(--muted)", margin: 0, lineHeight: 1.45 },

  toast: {
    position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
    background: "var(--ink)", color: "#fff", padding: "12px 20px", borderRadius: 13, fontSize: 14.5,
    fontWeight: 530, zIndex: 60, boxShadow: "0 8px 30px rgba(0,0,0,.25)", maxWidth: "90vw",
  },

  // ---- additions beyond the prototype ----

  // Breed picker (customer)
  breedRow: { display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 8 },
  breedSelect: {
    flex: 1, minWidth: 200, padding: "11px 13px", fontSize: 15.5, borderRadius: 11,
    border: "1.5px solid var(--line)", background: "var(--surface)", color: "var(--ink)", fontWeight: 530,
  },
  breedMeta: { fontSize: 13.5, color: "var(--muted)", margin: "2px 0 0" },

  // Admin breed manager
  breedTable: { display: "flex", flexDirection: "column", gap: 2 },
  breedItem: {
    display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 10, alignItems: "center",
    padding: "10px 0", borderBottom: "1px solid var(--line)",
  },
  breedName: { fontSize: 14.5, fontWeight: 540, border: "1.5px solid transparent", borderRadius: 8, padding: "6px 8px", background: "transparent", color: "var(--ink)", width: "100%" },
  numInput: {
    width: 64, padding: "7px 8px", fontSize: 14, borderRadius: 8, border: "1.5px solid var(--line)",
    background: "var(--surface)", color: "var(--ink)", textAlign: "center",
  },
  numLabel: { fontSize: 11, color: "var(--muted)", textAlign: "center", display: "block", marginBottom: 3 },

  // Branding / theming
  logoPreviewWrap: { display: "flex", alignItems: "center", gap: 14, marginBottom: 14 },
  logoPreview: { width: 56, height: 56, borderRadius: 14, objectFit: "cover", border: "1px solid var(--line)", background: "var(--accent-soft)" },
  presetRow: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 },
  preset: {
    display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10,
    border: "1.5px solid var(--line)", background: "var(--surface)", fontSize: 14, fontWeight: 540, color: "var(--ink)",
  },
  presetOn: { borderColor: "var(--accent)", boxShadow: "0 0 0 2px var(--accent-soft)" },
  presetDots: { display: "flex", gap: 3 },
  presetDot: { width: 12, height: 12, borderRadius: "50%" },
  tokenGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 12, marginTop: 8 },
  tokenItem: { display: "flex", alignItems: "center", gap: 8 },
  tokenColor: { width: 34, height: 34, borderRadius: 8, border: "1px solid var(--line)", padding: 0, cursor: "pointer", background: "none" },
  tokenHex: {
    width: 90, padding: "7px 8px", fontSize: 13, borderRadius: 8, border: "1.5px solid var(--line)",
    background: "var(--surface)", color: "var(--ink)", fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace",
  },
  tokenLabel: { fontSize: 12.5, color: "var(--muted)" },
  warnBox: {
    display: "flex", flexDirection: "column", gap: 4, background: "#FBF0EE", border: "1px solid #E7C9C4",
    borderRadius: 12, padding: "12px 14px", marginTop: 14,
  },
  warnTitle: { fontSize: 13.5, fontWeight: 620, color: "var(--danger)" },
  warnItem: { fontSize: 13, color: "var(--ink)" },
  okBox: {
    display: "flex", gap: 8, alignItems: "center", background: "var(--accent-soft)",
    borderRadius: 12, padding: "10px 14px", marginTop: 14, fontSize: 13.5, color: "var(--ink)",
  },

  // Auth gate
  authWrap: { display: "grid", placeItems: "center", minHeight: "70vh" },
  authCard: {
    background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 22,
    padding: "clamp(26px,4vw,40px)", maxWidth: 400, width: "100%", boxShadow: "var(--shadow)",
  },
  spinnerWrap: { display: "grid", placeItems: "center", minHeight: "60vh", color: "var(--muted)", fontSize: 15 },

  errorBox: {
    display: "flex", gap: 10, alignItems: "flex-start", background: "#FBF0EE", border: "1px solid #E7C9C4",
    borderRadius: 12, padding: "12px 14px", marginBottom: 16, fontSize: 14, color: "var(--ink)", lineHeight: 1.45,
  },
};
